import { useEffect, useRef, useState } from "react";
import { useCurrentPhaseItems, useCurrentRoom, useInventory } from "@/state/selectors";
import { StrapiMedia } from "@/components/shared/StrapiMedia";
import { useGameStore } from "@/state/gameStore";
import { getItemAssetVariant } from "@/utils/itemUtils";
import { CoverCanvas } from "@/components/shared/CoverCanvas";
import {getFormatKey, resolveImageUrl} from "@/libs/assetPreloading/preloadConfig";

type Props = {
    onReady?: () => void;
};

export function RoomBackground({ onReady }: Props) {
    const roomVariant = useGameStore((state) => state.currentRoomVariant);
    const room = useCurrentRoom();
    const items = useInventory();
    const allPhaseItems = useCurrentPhaseItems();

    const videoRef = useRef<HTMLVideoElement | null>(null);
    const [animatedFailed, setAnimatedFailed] = useState(false);

    // Reset failure state when room changes
    useEffect(() => {
        setAnimatedFailed(false);
    }, [room?.documentId]);

    useEffect(() => {
        if (animatedFailed) {
            onReady?.();
        }
    }, [animatedFailed, onReady]);

    // Recover video after tab refocus
    useEffect(() => {
        const handleVisibility = () => {
            if (document.visibilityState !== "visible") return;

            const video = videoRef.current;
            if (!video || animatedFailed) return;

            if (video.readyState === 0 || video.paused) {
                video.play().catch(() => {
                    setAnimatedFailed(true);
                    return;
                });

                // If play() "succeeded" but decoder is dead, readyState won't progress
                setTimeout(() => {
                    if (video.readyState < 2) {
                        setAnimatedFailed(true);
                    }
                }, 300);
            }
        };

        document.addEventListener("visibilitychange", handleVisibility);
        return () => document.removeEventListener("visibilitychange", handleVisibility);
    }, [animatedFailed]);

    if (!room?.image?.width || !room?.image?.height) {
        return null;
    }

    const hasAnimated = !!room.animated && !animatedFailed;

    const format = room.image.formats?.[getFormatKey("lg")];
    const imageW = format?.width ?? room.image.width;
    const imageH = format?.height ?? room.image.height;

    return (
        <CoverCanvas
            imageW={imageW}
            imageH={imageH}
            className="absolute inset-0 pointer-events-none"
        >
            {/* Static image — always rendered as fallback */}
            <StrapiMedia
                size="lg"
                image={room.image}
                alt={room.name}
                className="absolute inset-0 size-full"
                objectFit="fill"
                useImageAspectRatio={false}
                priority
            />

            {/* Animated layer on top */}
            {hasAnimated && (
                <StrapiMedia
                    size="lg"
                    image={room.animated}
                    alt={room.name}
                    className="absolute inset-0 size-full"
                    objectFit="fill"
                    useImageAspectRatio={false}
                    priority
                    videoRef={videoRef}
                    onLoad={onReady}
                    onError={() => setAnimatedFailed(true)}
                />
            )}

            {items.map((itemId) => {
                const item = allPhaseItems.find((i) => i.documentId === itemId);
                if (!item || !roomVariant) return null;

                const image = getItemAssetVariant(item, roomVariant);
                return (
                    <StrapiMedia
                        size="lg"
                        key={item.documentId}
                        image={image}
                        alt={room.name}
                        className="absolute inset-0 size-full"
                        objectFit="fill"
                        useImageAspectRatio={false}
                        priority
                    />
                );
            })}
        </CoverCanvas>
    );
}